export const GUIDED_TOUR_STORAGE_KEYS = {
    organizationCreated: 'acceliqa.onboarding.organizationCreated',
    billingDecisionCompleted: 'acceliqa.onboarding.billingDecisionCompleted',
    completed: 'acceliqa.tour.dashboardGuidedTourCompleted',
    skipped: 'acceliqa.tour.dashboardGuidedTourSkipped',
    replayRequested: 'acceliqa.tour.dashboardGuidedTourReplayRequested',
    currentStep: 'acceliqa.tour.dashboardGuidedTourCurrentStep',
    projectRoute: 'acceliqa.tour.dashboardGuidedTourProjectRoute',
};

function canUseLocalStorage(): boolean
{
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function getTourStorageValue(key: string): string | null
{
    if (!canUseLocalStorage())
    {
        return null;
    }

    try
    {
        return window.localStorage.getItem(key);
    }
    catch
    {
        return null;
    }
}

export function setTourStorageValue(key: string, value: string): void
{
    if (!canUseLocalStorage())
    {
        return;
    }

    try
    {
        window.localStorage.setItem(key, value);
    }
    catch
    {
        /* Storage failures should not block core product actions. */
    }
}

export function removeTourStorageValue(key: string): void
{
    if (!canUseLocalStorage())
    {
        return;
    }

    try
    {
        window.localStorage.removeItem(key);
    }
    catch
    {
        /* Storage failures should not block core product actions. */
    }
}

export function getStoredTourStepIndex(): number
{
    const rawStep = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.currentStep);
    const parsed = Number(rawStep ?? 0);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function setStoredTourStepIndex(stepIndex: number): void
{
    setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.currentStep, String(Math.max(0, stepIndex)));
}

export function markOnboardingOrganizationCreated(nextStepIndex = 1): void
{
    setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.organizationCreated, 'true');
    setStoredTourStepIndex(nextStepIndex);
}

export function markOnboardingBillingDecisionCompleted(nextStepIndex = 2): void
{
    setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted, 'true');
    setStoredTourStepIndex(nextStepIndex);
}

export function markGuidedTourProjectCreated(projectId: number, nextStepIndex = 3): void
{
    setTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.projectRoute, `/projects/${projectId}?tab=requirements`);
    setStoredTourStepIndex(nextStepIndex);
}

export function isDashboardGuidedTourDismissed(): boolean
{
    return getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.completed) === 'true'
        || getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.skipped) === 'true';
}

export function isDashboardGuidedTourInProgress(): boolean
{
    if (isDashboardGuidedTourDismissed())
    {
        return false;
    }

    return getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.replayRequested) === 'true'
        || getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.organizationCreated) === 'true'
        || getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';
}
